import { ChangeEvent } from 'react';
import { FormField } from '@nimbus-ds/patterns';
import { WeightRestrictionType } from '@tiendanube/common/src/enums';
import { InputNumberNimbus, Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import OptionsSection from '../../../OptionsSection';
import { RadioOption } from '../../../shared/types';

interface WeightRestrictionSectionProps {
  cartWeight: WeightRestrictionType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  cartWeightMin?: string;
  cartWeightMax?: string;
  errors?: Partial<{
    cartWeightMin: string;
    cartWeightMax: string;
  }>;
}

function WeightRestrictionSection({
  cartWeight,
  onChange,
  cartWeightMin,
  cartWeightMax,
  errors = {},
}: Readonly<WeightRestrictionSectionProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.restrictions',
  );

  const cartWeightOptions: RadioOption[] = [
    {
      label: t('cartWeight.allWeights'),
      value: WeightRestrictionType.ALL_WEIGHTS,
    },
    {
      label: t('cartWeight.weightRange'),
      value: WeightRestrictionType.WEIGHT_RANGE,
    },
  ];

  return (
    <Stack column align="stretch">
      <Stack.Item>
        <OptionsSection
          title={t('cartWeight.title')}
          name="weightRestrictionType"
          options={cartWeightOptions}
          value={cartWeight}
          onChange={onChange}
        />
      </Stack.Item>

      {cartWeight === WeightRestrictionType.WEIGHT_RANGE && (
        <Stack.Item>
          <Stack wrap align="stretch">
            <Stack.Item fill>
              <FormField
                label={t('cartWeight.from')}
                showHelpText
                helpText={
                  errors.cartWeightMin ? t(errors.cartWeightMin) : undefined
                }
              >
                <InputNumberNimbus
                  type="float"
                  name="cartWeightMin"
                  value={cartWeightMin ?? ''}
                  onChange={onChange}
                  append={t('cartWeight.unit')}
                  appendPosition="end"
                  appearance={errors.cartWeightMin ? 'danger' : 'neutral'}
                />
              </FormField>
            </Stack.Item>

            <Stack.Item fill>
              <FormField
                label={t('cartWeight.to')}
                showHelpText
                helpText={
                  errors.cartWeightMax ? t(errors.cartWeightMax) : undefined
                }
              >
                <InputNumberNimbus
                  type="float"
                  name="cartWeightMax"
                  value={cartWeightMax ?? ''}
                  onChange={onChange}
                  append={t('cartWeight.unit')}
                  appendPosition="end"
                  appearance={errors.cartWeightMax ? 'danger' : 'neutral'}
                />
              </FormField>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      )}
    </Stack>
  );
}

export default WeightRestrictionSection;
